/**
 * UserNotificationCopySection — admin clona preferências de notificação
 * de outro user pro user editado (TD-V2-UX-01, 2026-05-26).
 *
 * Antes: ficava em /perfil/notificacoes. Removido — self-service "copiar
 * de quem?" exige conhecer outro user; admin tem visão dos times e quem
 * clonar. Backend endpoint segue 403 pra non-admin.
 *
 * Usage: renderizado em UserFormDialog em modo edit (user já existe — a
 * cópia escreve em prefs do user editado, source é outro user qualquer).
 */

import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { Alert, Button, Card, Select, useToast } from '@/components/ui';
import { useUsers } from '@/lib/admin-store';
import { api, ApiError } from '@/lib/api';

interface UserNotificationCopySectionProps {
  /** ID do user EDITADO (target da cópia). */
  targetUserId: string;
}

export function UserNotificationCopySection({
  targetUserId,
}: UserNotificationCopySectionProps) {
  const allUsers = useUsers();
  const otherUsers = allUsers.filter((u) => u.id !== targetUserId);
  const [source, setSource] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { show: showToast } = useToast();
  const qc = useQueryClient();

  async function handleCopy() {
    if (!source) return;
    setPending(true);
    setError(null);
    try {
      const res = await api.post<{ ok: true; copied: number }>(
        `/api/v1/notification-preferences/${encodeURIComponent(targetUserId)}/copy-from/${encodeURIComponent(source)}`,
      );
      showToast(
        `${res.copied} ${res.copied === 1 ? 'escolha copiada' : 'escolhas copiadas'}`,
      );
      // Invalida cache do user editado pra que NotificationsPage dele reflete na sessão dele
      void qc.invalidateQueries({
        queryKey: ['v1', 'notification-preferences', targetUserId],
      });
      setSource('');
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setError('Só quem é administrador pode copiar as preferências de outra pessoa.');
      } else if (err instanceof ApiError && err.status === 404) {
        setError('Não encontramos a pessoa escolhida. Atualize a página e tente de novo.');
      } else {
        setError(
          err instanceof Error
            ? `Não foi possível copiar. Detalhe: ${err.message}`
            : 'Não foi possível copiar. Tente de novo.',
        );
      }
    } finally {
      setPending(false);
    }
  }

  return (
    <Card variant="flat" className="min-w-0" data-testid="user-notif-copy-section">
      <div className="min-w-0 space-y-2 p-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-ink-1 dark:text-ink-dark-1">
            Copiar os avisos de outra pessoa
          </p>
          <p className="mt-0.5 text-xs text-ink-3 dark:text-ink-dark-3">
            Troca as escolhas de aviso desta pessoa pelas de quem você escolher (os avisos dentro
            do sistema ficam sempre ligados). Ajuda a configurar quem acabou de entrar.
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Select
            size="sm"
            value={source}
            onChange={(e) => setSource(e.target.value)}
            wrapperClassName="min-w-0 flex-1"
            aria-label="Pessoa de quem copiar os avisos"
            data-testid="user-notif-copy-select"
          >
            <option value="">Selecione uma pessoa…</option>
            {otherUsers.map((u) => (
              <option key={u.id} value={u.id}>
                {u.nome}
              </option>
            ))}
          </Select>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={handleCopy}
            disabled={!source || pending}
            loading={pending}
            data-testid="user-notif-copy-submit"
          >
            Copiar
          </Button>
        </div>
        {error && (
          <Alert
            variant="danger"
            rail
            className="mt-2 text-xs"
            data-testid="user-notif-copy-error"
          >
            {error}
          </Alert>
        )}
      </div>
    </Card>
  );
}
